/**
 * Utilidades para cálculos estadísticos avanzados
 */

/**
 * Calcula el percentil de un conjunto de datos
 * @param {number[]} data - Array de valores numéricos
 * @param {number} percentile - Percentil a calcular (0-100)
 * @returns {number} Valor del percentil
 */
export function calculatePercentile(data, percentile) {
	if (!Array.isArray(data) || data.length === 0) return 0;
	const sorted = [...data].sort((a, b) => a - b);
	const index = (percentile / 100) * (sorted.length - 1);
	const lower = Math.floor(index);
	const upper = Math.ceil(index);
	if (lower === upper) return sorted[lower] || 0;
	const result = (sorted[lower] * (upper - index)) + (sorted[upper] * (index - lower));
	return (typeof result === 'number' && !isNaN(result) && isFinite(result)) ? result : 0;
}

/**
 * Calcula los cuartiles (Q1, Q2/Mediana, Q3)
 * @param {number[]} data - Array de valores numéricos
 * @returns {Object} Objeto con q1, q2, q3
 */
export function calculateQuartiles(data) {
	return {
		q1: calculatePercentile(data, 25),
		q2: calculatePercentile(data, 50),
		q3: calculatePercentile(data, 75),
	};
}

/**
 * Calcula la desviación estándar
 * @param {number[]} data - Array de valores numéricos
 * @returns {number} Desviación estándar
 */
export function calculateStdDev(data) {
	if (!Array.isArray(data) || data.length === 0) return 0;
	const mean = data.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0) / data.length;
	const variance = data.reduce((sum, val) => sum + ((val - mean) ** 2), 0) / data.length;
	const result = Math.sqrt(variance);
	return (typeof result === 'number' && !isNaN(result) && isFinite(result)) ? result : 0;
}

/**
 * Calcula el coeficiente de variación (CV)
 * @param {number[]} data - Array de valores numéricos
 * @returns {number} Coeficiente de variación como porcentaje
 */
export function calculateCV(data) {
	if (!Array.isArray(data) || data.length === 0) return 0;
	const mean = data.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0) / data.length;
	const stdDev = calculateStdDev(data);
	return mean !== 0 ? (stdDev / Math.abs(mean)) * 100 : 0;
}

/**
 * Calcula la mediana
 * @param {number[]} data - Array de valores numéricos
 * @returns {number} Mediana
 */
export function calculateMedian(data) {
	return calculatePercentile(data, 50);
}

/**
 * Calcula el rango intercuartil (IQR)
 * @param {number[]} data - Array de valores numéricos
 * @returns {number} Rango intercuartil
 */
export function calculateIQR(data) {
	const { q1, q3 } = calculateQuartiles(data);
	const result = q3 - q1;
	return (typeof result === 'number' && !isNaN(result) && isFinite(result)) ? result : 0;
}

/**
 * Detecta outliers usando el método IQR
 * @param {number[]} data - Array de valores numéricos
 * @returns {number[]} Array de valores considerados outliers
 */
export function detectOutliers(data) {
	if (!Array.isArray(data) || data.length === 0) return [];
	const { q1, q3 } = calculateQuartiles(data);
	const iqr = q3 - q1;
	const lowerBound = q1 - (1.5 * iqr);
	const upperBound = q3 + (1.5 * iqr);
	return data.filter(val => typeof val === 'number' && (val < lowerBound || val > upperBound));
}

/**
 * Calcula el mínimo y máximo de un conjunto de datos
 * @param {number[]} data - Array de valores numéricos
 * @returns {Object} Objeto con min y max
 */
export function calculateMinMax(data) {
	if (!Array.isArray(data) || data.length === 0) return { min: 0, max: 0 };
	const validData = data.filter(val => typeof val === 'number' && !isNaN(val) && isFinite(val));
	if (validData.length === 0) return { min: 0, max: 0 };
	return {
		min: Math.min(...validData),
		max: Math.max(...validData),
	};
}

/**
 * Calcula el promedio de un conjunto de datos
 * @param {number[]} data - Array de valores numéricos
 * @returns {number} Promedio
 */
export function calculateMean(data) {
	if (!Array.isArray(data) || data.length === 0) return 0;
	const total = data.reduce((accumulator, val) => accumulator + (typeof val === 'number' ? val : 0), 0);
	const result = total / data.length;
	return (typeof result === 'number' && !isNaN(result) && isFinite(result)) ? result : 0;
}



// WEBPACK FOOTER //
// ./src/shared/statistics-utils.js